import React, { useCallback, useRef, useState } from 'react';
import API_CONFIG from '../../apiConfig';
import { useAuth } from '../../context/AuthContext';
import { examplePresets, ExamplePreset } from './examplePresets';

interface ImageUploadProps {
  imageUrls: string[];
  setImageUrls: React.Dispatch<React.SetStateAction<string[]>>;
  isExample?: boolean;
  onExampleSelect?: (presetId: number) => void;
}

const ImageUpload: React.FC<ImageUploadProps> = ({ imageUrls, setImageUrls, isExample = false, onExampleSelect }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { currentUser } = useAuth();

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    setUploadStatus(null);

    // Only take the first image since Wrinkle Removal takes 1 image input
    const file = files[0];
    const formData = new FormData();
    formData.append('files', file);
    formData.append('userId', currentUser?.uid || 'anonymous');

    try {
      const response = await fetch(API_CONFIG.AI_WRINKLE_REMOVAL_UPLOAD_URL, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        setUploadStatus('Upload failed. Please try again.');
        setImageUrls([]);
        return;
      }

      const data = await response.json();

      if (data.success) {
        if (data.uploaded && Array.isArray(data.uploaded) && data.uploaded.length > 0) {
          setImageUrls([data.uploaded[0].url]);
          setUploadStatus('Upload successful!');
        } else if (data.urls && Array.isArray(data.urls) && data.urls.length > 0) {
          setImageUrls([data.urls[0]]);
          setUploadStatus('Upload successful!');
        } else if (data.url) {
          setImageUrls([data.url]);
          setUploadStatus('Upload successful!');
        } else {
          setUploadStatus('Upload failed. No file URL returned.');
          setImageUrls([]);
        }
      } else {
        setUploadStatus(data.message || 'Upload failed. Please try again.');
        setImageUrls([]);
      }
    } catch (error) {
      setUploadStatus('Upload failed. Please try again.');
      setImageUrls([]);
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    handleFiles(e.dataTransfer.files);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files);
  };

  const handleExampleClick = (presetId: number) => {
    if (imageUrls.length > 0) return;
    if (onExampleSelect) {
      onExampleSelect(presetId);
    }
  };

  const hasUploadedImages = imageUrls.length > 0;

  return (
    <div className="bg-accent-sand rounded-lg p-4">
      <div className="space-y-4">
        {/* Upload area or preview */}
        {hasUploadedImages ? (
          <div className="bg-white rounded-lg p-4 border-2 border-dashed border-accent-taupe flex flex-col items-center justify-center relative h-[400px]">
            {/* Clear image button */}
            <button
              className="absolute top-2 right-2 text-text-description hover:text-red-500 text-2xl focus:outline-none z-10"
              onClick={() => {
                setImageUrls([]);
                setUploadStatus(null);
              }}
              aria-label="Remove image"
            >
              &times;
            </button>
            <h3 className="font-medium text-text-heading mb-3">
              {isExample ? 'Example Wrinkled Garment' : 'Wrinkled Garment Image'}
            </h3>
            <div className="flex justify-center items-center w-full h-full">
              <img
                src={imageUrls[0]}
                alt={isExample ? 'Example wrinkled garment' : 'Uploaded wrinkled garment'}
                className="w-4/5 object-contain rounded shadow-md max-h-[300px] bg-white"
              />
            </div>
            {!isExample && uploadStatus && (
              <p className="text-green-600 mt-3 text-sm">Upload successful!</p>
            )}
            {isExample && (
              <p className="text-text-description mt-3 text-xs text-center">
                This is an example image. Upload your garment image to remove wrinkles.
              </p>
            )}
          </div>
        ) : (
          <div
            className="bg-accent-sand rounded-lg p-6 border-2 border-dashed border-accent-taupe flex flex-col items-center justify-center h-[400px]"
            onDrop={handleDrop}
            onDragOver={handleDragOver}
          >
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={handleFileChange}
              accept="image/*"
              multiple={false}
            />
            <div className="w-16 h-16 bg-secondary-100 rounded-full flex items-center justify-center mb-4">
              <i className="fas fa-wand-magic-sparkles text-secondary-600 text-2xl"></i>
            </div>
            <h3 className="font-medium text-text-heading mb-2">Upload Wrinkled Garment Image</h3>
            <p className="text-sm text-text-description mb-4 text-center">
              Drag & drop 1 garment image or click to browse.
              <span className="block mt-1 text-xs">
                Over 4000px images are scaled to 4000px to run efficiently.
              </span>
            </p>
            {uploading ? (
              <div className="flex items-center justify-center h-12">
                <span className="inline-block w-8 h-8 border-4 border-secondary-500 border-t-transparent rounded-full animate-spin"></span>
                <span className="ml-2 text-sm text-text-description">Upload in progress...</span>
              </div>
            ) : (
              <button
                className="bg-secondary-500 hover:bg-secondary-600 text-white px-6 py-2 rounded-lg font-medium"
                onClick={handleButtonClick}
                disabled={uploading}
              >
                Select Image
              </button>
            )}
            <p className="text-xs text-text-description mt-3">JPG, PNG, WebP up to 10MB</p>
            {uploadStatus && (
              <p className={`mt-2 text-sm ${uploadStatus.includes('successful') ? 'text-green-600' : 'text-red-600'}`}>
                {uploadStatus}
              </p>
            )}
          </div>
        )}

        {/* Examples */}
        <div className="mt-6">
          <h4 className={`text-sm font-medium mb-3 ${hasUploadedImages ? 'text-text-description' : 'text-text-heading'}`}>
            Or try an example:
          </h4>
          <div className={`grid ${examplePresets.length === 1 ? 'grid-cols-1' : 'grid-cols-2'} gap-3`}>
            {examplePresets.map((preset: ExamplePreset) => (
              <button
                key={preset.id}
                onClick={() => handleExampleClick(preset.id)}
                disabled={hasUploadedImages}
                className={`group relative rounded-lg overflow-hidden border border-accent-taupe bg-white ${
                  hasUploadedImages ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-secondary-500'
                }`}
              >
                <img
                  src={preset.preview}
                  alt={preset.name}
                  className={`w-full h-32 object-contain transition-opacity ${
                    hasUploadedImages ? 'opacity-50 grayscale' : 'group-hover:opacity-75'
                  }`}
                />
                <div className={`absolute inset-0 transition-opacity ${
                  hasUploadedImages ? 'bg-accent-sand bg-opacity-20' : 'bg-black bg-opacity-0 group-hover:bg-opacity-20'
                }`} />
                <p className={`absolute bottom-1 left-1 right-1 text-xs text-center rounded px-1 py-0.5 ${
                  hasUploadedImages ? 'text-text-description bg-accent-sand bg-opacity-50' : 'text-white bg-black bg-opacity-50'
                }`}>
                  {preset.name}
                </p>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageUpload;
